import React, { useCallback, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { toast } from "react-toastify";

const API_BASE = "https://qr.billsarthi.in/public/api";

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
};

const formatAmount = (value) => {
  const num = Number(value || 0);
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `₹ ${num.toFixed(2)}`;
  }
};

const DeletedOrdersPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [restaurantId, setRestaurantId] = useState(null);
  const [confirmingLog, setConfirmingLog] = useState(null);

  const fetchLogs = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        const stored = localStorage.getItem("userData");
        const parsed = stored ? JSON.parse(stored) : null;
        const id = parsed?.data?.id || parsed?.id;
        setRestaurantId(id || null);
        if (!id) {
          setLogs([]);
          return;
        }
        const res = await fetch(`${API_BASE}/deleted-orders/list`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: id }),
        });
        const result = await res.json();
        if (!res.ok) {
          throw new Error(result?.message || "Failed to fetch deleted orders");
        }
        setLogs(Array.isArray(result?.data) ? result.data : []);
      } catch (error) {
        toast.error(error?.message || "Network error");
      } finally {
        if (!silent) setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const restoreOrder = async (logId) => {
    if (!restaurantId) return;
    try {
      setRestoringId(logId);
      const res = await fetch(`${API_BASE}/deleted-orders/restore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: restaurantId, log_id: logId }),
      });
      const result = await res.json();
      if (!res.ok) {
        throw new Error(result?.message || "Failed to restore order");
      }
      toast.success("Order restored");
      fetchLogs(true);
    } catch (error) {
      toast.error(error?.message || "Network error");
    } finally {
      setRestoringId(null);
    }
  };

  const confirmRestore = (log) => setConfirmingLog(log);
  const cancelRestore = () => setConfirmingLog(null);
  const handleConfirmRestore = () => {
    if (confirmingLog) {
      restoreOrder(confirmingLog.logId);
      setConfirmingLog(null);
    }
  };

  return (
    <Page>
      <Inner>
        <HeaderRow>
          <div>
            <HeaderTitle>Deleted Orders</HeaderTitle>
            <HeaderSubtitle>Restore removed bills quickly</HeaderSubtitle>
          </div>
          <HeaderActions>
            <ActionBtn
              type="button"
              onClick={() => {
                setRefreshing(true);
                fetchLogs(true);
              }}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </ActionBtn>
          </HeaderActions>
        </HeaderRow>

        {loading ? (
          <LoaderWrap>
            <Spinner />
            <div>Loading deleted orders…</div>
          </LoaderWrap>
        ) : logs.length === 0 ? (
          <EmptyState>
            <div className="title">No deleted orders found</div>
            <div className="subtitle">
              Orders you delete will appear here for restore.
            </div>
          </EmptyState>
        ) : (
          <CardGrid>
            {logs.map((item) => {
              const order = item?.order || {};
              const orderCode =
                order?.ordercode ||
                order?.order_code ||
                order?.id ||
                item?.order_id;
              const tableId = order?.tableid || order?.table_id || "-";
              const amount =
                order?.totalamount ?? order?.total_amount ?? order?.amount ?? 0;
              const mode = order?.mode || order?.paymentmode;
              const isBusy = restoringId === item?.log_id;
              return (
                <Card key={item?.log_id}>
                  <CardHeader>
                    <div className="order">
                      Order {orderCode ? `#${orderCode}` : ""}
                    </div>
                    <div className="deletedAt">
                      {formatDateTime(item?.deleted_at)}
                    </div>
                  </CardHeader>
                  <MetaRow>
                    <span>Table: {tableId}</span>
                    <span>Items: {item?.items_count ?? 0}</span>
                  </MetaRow>
                  <MetaRow>
                    <span>Amount: {formatAmount(amount)}</span>
                    {mode ? <span>Mode: {mode}</span> : <span />}
                  </MetaRow>
                  <RestoreBtn
                    type="button"
                    onClick={() =>
                      confirmRestore({ logId: item?.log_id, orderCode })
                    }
                    disabled={isBusy}
                  >
                    {isBusy ? "Restoring…" : "Restore"}
                  </RestoreBtn>
                </Card>
              );
            })}
          </CardGrid>
        )}
      </Inner>

      {confirmingLog ? (
        <ModalOverlay role="dialog" aria-modal="true">
          <ModalCard>
            <ModalTitle>Restore Order?</ModalTitle>
            {confirmingLog?.orderCode ? (
              <ModalSub>Order #{confirmingLog.orderCode}</ModalSub>
            ) : null}
            <ModalText>Do you want to restore this order?</ModalText>
            <ModalActions>
              <ModalBtn type="button" onClick={cancelRestore}>
                Cancel
              </ModalBtn>
              <ModalBtn
                type="button"
                $primary
                onClick={handleConfirmRestore}
              >
                Restore
              </ModalBtn>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </Page>
  );
};

export default DeletedOrdersPage;

const Page = styled.div`
  min-height: 100vh;
  background: #f4f6fb;
  padding: 16px 16px 28px;
  display: flex;
  justify-content: center;
`;

const Inner = styled.div`
  width: 100%;
  max-width: 1200px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
`;

const HeaderSubtitle = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #64748b;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionBtn = styled.button`
  border: 1px solid #d6deea;
  background: #ffffff;
  color: #0f172a;
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 700;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  .order {
    font-weight: 800;
    color: #0f172a;
  }
  .deletedAt {
    font-size: 12px;
    color: #94a3b8;
  }
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  color: #475569;
  font-size: 13px;
  gap: 8px;
  span {
    min-width: 0;
  }
`;

const RestoreBtn = styled.button`
  margin-top: 8px;
  background: #015b9a;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 12px;
  font-weight: 800;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoaderWrap = styled.div`
  display: grid;
  place-items: center;
  padding: 40px 0;
  color: #475569;
  gap: 10px;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid #dbeafe;
  border-top-color: #015b9a;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const EmptyState = styled.div`
  background: #fff;
  border: 1px dashed #dbe2ee;
  border-radius: 16px;
  padding: 30px 20px;
  text-align: center;
  color: #64748b;
  .title {
    font-weight: 800;
    color: #0f172a;
  }
  .subtitle {
    margin-top: 6px;
    font-size: 13px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: grid;
  place-items: center;
  z-index: 999;
  padding: 16px;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 20px;
  padding: 22px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
`;

const ModalTitle = styled.div`
  font-size: 18px;
  font-weight: 800;
  text-align: center;
  color: #0f172a;
`;

const ModalSub = styled.div`
  margin-top: 4px;
  font-size: 13px;
  text-align: center;
  color: #64748b;
`;

const ModalText = styled.div`
  margin-top: 12px;
  font-size: 14px;
  text-align: center;
  color: #475569;
`;

const ModalActions = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const ModalBtn = styled.button`
  border-radius: 10px;
  padding: 10px 12px;
  font-weight: 800;
  border: 1px solid #e2e8f0;
  background: ${({ $primary }) => ($primary ? "#015b9a" : "#f1f5f9")};
  color: ${({ $primary }) => ($primary ? "#fff" : "#0f172a")};
  cursor: pointer;
`;
